import { Component, type ErrorInfo, type ReactNode } from 'react';

// TOP-LEVEL REACT ERROR BOUNDARY. Without one, a render-time throw ANYWHERE unmounts the entire app to
// a blank white screen with nothing but a console error — the dashboard equivalent of the Worker's
// unhandled 500. This catches it, logs it in the same structured JSON shape as the API's log.ts (the
// closest the browser has), and shows a recoverable message instead of a void. Native choice — no
// external error-reporting vendor (Sentry was explicitly not adopted).
interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      JSON.stringify({
        level: 'error',
        event: 'react_error_boundary',
        error: error.message,
        componentStack: info.componentStack,
      })
    );
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          padding: 24,
          textAlign: 'center',
          font: '14px system-ui',
        }}
      >
        <div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Something went wrong.</p>
          <p style={{ color: '#71717a', marginBottom: 16 }}>{error.message}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: '1px solid #d4d4d8',
              cursor: 'pointer',
              background: 'transparent',
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
